import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import axiosInstance from "../../api/axiosInstance";
import { getUserId } from "../../utils/getUserId";
import { buildFisHTML } from "./fis/HesapIslemFisHTMLBuilderSadeceGram";
import {
  shouldUseBluetoothPrint,
  isBluetoothPrinterConnected,
  connectBluetoothPrinter,
  printBluetoothFis,
  printHtmlFallback,
} from "./fis/BluetoothEscPosService";
import { usePermission } from "../../context/PermissionContext";

import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";

import KuyumcuLeftSideNavbar from "./components/KuyumcuLeftSideNavbar";
import PremiumBackground from "./components/PremiumBackground";

import ProsetBackButton from "./components/ProsetBackButton";
import SilmeUyariPopup from "./components/SilmeUyariPopup";

import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";

import { Add, Delete, Print } from "@mui/icons-material";

import "./css/KuyumcuMainPage.css";
import "./css/KuyumcuHesapIslemAltinDetayPage.css";

/**
 * datetime-local input için:
 * - backend ISO string (2026-01-22T01:38:25.486...) -> yyyy-MM-ddTHH:mm
 */
function toDateTimeLocalValue(isoString) {
  if (!isoString) return "";

  const d = new Date(isoString);

  if (Number.isNaN(d.getTime())) {
    return "";
  }

  const pad = (n) => (n < 10 ? `0${n}` : `${n}`);

  const yyyy = d.getFullYear();
  const MM = pad(d.getMonth() + 1);
  const dd = pad(d.getDate());
  const HH = pad(d.getHours());
  const mm = pad(d.getMinutes());

  return `${yyyy}-${MM}-${dd}T${HH}:${mm}`;
}

export default function KuyumcuHesapIslemAltinDetayPage() {
  const navigate = useNavigate();
  const { islemId } = useParams();

  const userId = getUserId();

  const token =
    localStorage.getItem("token") || sessionStorage.getItem("token");

  const { can, isLoading: permLoading } = usePermission();

  /* ===========================================================
     YETKİLER
  =========================================================== */

  const PERM_UPDATE = "HESAP_ISLEMLER_UPDATE";

  const PERM_DELETE = "HESAP_ISLEMLER_DELETE";

  const canUpdate = useMemo(
    () => !permLoading && can(PERM_UPDATE),
    [permLoading, can]
  );

  const canDelete = useMemo(
    () => !permLoading && can(PERM_DELETE),
    [permLoading, can]
  );

  /* ===========================================================
     STATE
  =========================================================== */

  const [loading, setLoading] = useState(false);

  /* ===========================================================
     MASTER
  =========================================================== */

  const [master, setMaster] = useState({
    id: null,
    hesapId: null,
    tarih: "",
    islemTur: "",
    aciklama: "",
  });

  /*
   * İlk yüklenen master kaydının snapshot'ı.
   *
   * Tarih, işlem türü veya açıklama değiştiğinde
   * Kaydet butonunu göstermek için kullanılır.
   */
  const [initialMaster, setInitialMaster] = useState({
    id: null,
    hesapId: null,
    tarih: "",
    islemTur: "",
    aciklama: "",
  });

  /* ===========================================================
     SATIRLAR
  =========================================================== */

  const [satirlar, setSatirlar] = useState([]);

  const [initialSatirlar, setInitialSatirlar] = useState([]);

  /* ===========================================================
     STOKLAR
  =========================================================== */

  const [stoklar, setStoklar] = useState([]);

  /* ===========================================================
     FİŞ BİLGİLERİ
  =========================================================== */

  const [firmaAdi, setFirmaAdi] = useState("");

  const [hesapAdi, setHesapAdi] = useState("");

  const [oncekiBakiye, setOncekiBakiye] = useState(null);

  const [sonrakiBakiye, setSonrakiBakiye] = useState(null);

  /* ===========================================================
     SİLME POPUP
  =========================================================== */

  const [silmePopupOpen, setSilmePopupOpen] = useState(false);

  /* ===========================================================
     BOŞ SATIR KAYDET POPUP
  =========================================================== */

  const [bosSatirKaydetPopupOpen, setBosSatirKaydetPopupOpen] = useState(false);

  const [pendingSavePayload, setPendingSavePayload] = useState(null);

  /* ===========================================================
     AUTH KONTROL
  =========================================================== */

  useEffect(() => {
    if (!userId || !token) {
      navigate("/login");
      return;
    }
  }, [userId, token, navigate]);

  /* ===========================================================
     LOAD
  =========================================================== */

  useEffect(() => {
    if (!userId || !token) {
      return;
    }

    if (!islemId) {
      return;
    }

    const loadAll = async () => {
      setLoading(true);

      try {
        /* =====================================================
           1) STOKLAR
        ===================================================== */

        const stokResp = await axiosInstance.get("/stoklar", {
          params: {
            userId,
          },
        });

        setStoklar(stokResp.data || []);

        /* =====================================================
           2) ALTIN DETAY
        ===================================================== */

        const detayResp = await axiosInstance.get(`/islemler/altin/${islemId}`);

        const data = detayResp.data || {};

        setFirmaAdi(data.firmaAdi || "");

        setHesapAdi(data.hesapAdi || "");

        setOncekiBakiye(
          typeof data.oncekiBakiyeGram === "number"
            ? data.oncekiBakiyeGram
            : null
        );

        setSonrakiBakiye(
          typeof data.sonrakiBakiyeGram === "number"
            ? data.sonrakiBakiyeGram
            : null
        );

        /* =====================================================
           MASTER
        ===================================================== */

        const m = data.master || {};

        const loadedMaster = {
          id: m.id ?? null,

          hesapId: m.hesapId ?? null,

          tarih: toDateTimeLocalValue(m.tarih),

          islemTur: m.islemTur || "",

          aciklama: m.aciklama || "",
        };

        setMaster(loadedMaster);

        setInitialMaster(loadedMaster);

        /* =====================================================
           SATIRLAR
        ===================================================== */

        const loadedSatirlar = (data.satirlar || []).map((s) => ({
          id: s.id ?? null,

          stokId: s.stokId ?? "",

          stokAdi: s.stokAdi || "",

          miktar: s.miktar ?? "",

          milyem: s.milyem ?? "",

          hasGram: s.hasGram ?? "",

          adet: s.adet ?? "",

          iscilik: s.iscilik ?? "",

          iscilikGram: s.iscilikGram ?? "",

          alinanIscilik: s.alinanIscilik ?? "",

          verilenIscilik: s.verilenIscilik ?? "",
        }));

        setSatirlar(loadedSatirlar);

        setInitialSatirlar(loadedSatirlar);
      } catch (err) {
        console.error("Altın işlem detay yükleme hatası:", err);

        alert("Altın işlem detayı yüklenemedi.");
      } finally {
        setLoading(false);
      }
    };

    loadAll();
  }, [userId, token, islemId]);

  /* ===========================================================
     KARŞILAŞTIRMA NORMALİZASYONU
  =========================================================== */

  const normalizeSatirlarForCompare = (list = []) => {
    return (list || []).map((s) => ({
      id: s.id ?? null,

      stokId: s.stokId === "" ? null : Number(s.stokId),

      miktar: s.miktar === "" || s.miktar == null ? null : Number(s.miktar),

      milyem: s.milyem === "" || s.milyem == null ? null : Number(s.milyem),

      hasGram: s.hasGram === "" || s.hasGram == null ? null : Number(s.hasGram),

      adet: s.adet === "" || s.adet == null ? null : Number(s.adet),

      iscilik: s.iscilik === "" || s.iscilik == null ? null : Number(s.iscilik),

      iscilikGram:
        s.iscilikGram === "" || s.iscilikGram == null
          ? null
          : Number(s.iscilikGram),

      alinanIscilik:
        s.alinanIscilik === "" || s.alinanIscilik == null
          ? null
          : Number(s.alinanIscilik),

      verilenIscilik:
              s.verilenIscilik === "" || s.verilenIscilik == null
                ? null
                : Number(s.verilenIscilik),
    }));
  };

  /* ===========================================================
     İŞLEM TÜRÜ LABEL
  =========================================================== */

  const renderIslemTurLabel = (tur) => {
    if (!tur) {
      return "";
    }

    if (tur === "ALTIN_ALINDI") {
      return "Altın Alındı";
    }

    if (tur === "ALTIN_VERILDI") {
      return "Altın Verildi";
    }

    return tur;
  };

  /* ===========================================================
     MASTER DEĞİŞİKLİĞİ
  =========================================================== */

  const hasMasterChanges = useMemo(() => {
    const initial = {
      tarih: initialMaster.tarih || "",

      islemTur: initialMaster.islemTur || "",

      aciklama: initialMaster.aciklama || "",
    };

    const current = {
      tarih: master.tarih || "",

      islemTur: master.islemTur || "",

      aciklama: master.aciklama || "",
    };

    return JSON.stringify(initial) !== JSON.stringify(current);
  }, [initialMaster, master]);

  /* ===========================================================
     SATIR DEĞİŞİKLİĞİ
  =========================================================== */

  const hasSatirChanges = useMemo(() => {
    const initial = normalizeSatirlarForCompare(initialSatirlar);

    const current = normalizeSatirlarForCompare(satirlar);

    return JSON.stringify(initial) !== JSON.stringify(current);
  }, [initialSatirlar, satirlar]);

  const hasChanges = hasMasterChanges || hasSatirChanges;

  /* ===========================================================
     SATIR EKLE
  =========================================================== */

  const handleAddRow = () => {
    if (!canUpdate) {
      return;
    }

    setSatirlar((prev) => [
      ...prev,
      {
        id: null,
        stokId: "",
        stokAdi: "",
        miktar: "",
        milyem: "",
        hasGram: "",
        adet: "",
        iscilik: "",
        iscilikGram: "",
        alinanIscilik: "",
        verilenIscilik: "",
      },
    ]);
  };

  /* ===========================================================
     SATIR SİL
  =========================================================== */

  const handleDeleteRow = (index) => {
    if (!canUpdate) {
      return;
    }

    setSatirlar((prev) => prev.filter((_, i) => i !== index));
  };

  /* ===========================================================
     STOK DEĞİŞTİR + SON İŞÇİLİĞİ GETİR
  =========================================================== */

  const handleStokChange = async (index, stokIdVal) => {
    const stokIdNum =
      stokIdVal === "" || stokIdVal === null || stokIdVal === undefined
        ? ""
        : Number(stokIdVal);

    const stok = stoklar.find((s) => Number(s.id) === Number(stokIdNum));

    /*
     * Stok değişince önce yeni stok bilgisini yaz.
     * Eski stoktan kalan işçilik ve işçilik gram temizlensin.
     */
    setSatirlar((prev) =>
      prev.map((row, i) =>
        i === index
          ? {
              ...row,
              stokId: stokIdNum,
              stokAdi: stok ? stok.stokAdi : "",
              iscilik: "",
              iscilikGram: "",
            }
          : row
      )
    );

    /*
     * Stok seçimi temizlendiyse sorgu yapma.
     */
    if (!stokIdNum) {
      return;
    }

    /*
     * Son işçilik hesap + stok + işlem türüne göre aranır.
     */
    if (!master.hesapId || !master.islemTur) {
      return;
    }

    if (
      master.islemTur !== "ALTIN_ALINDI" &&
      master.islemTur !== "ALTIN_VERILDI"
    ) {
      return;
    }

    const currentUserId = Number(userId);

    if (!currentUserId) {
      console.error(
        "Son işçilik sorgulanamadı: geçerli kullanıcı ID bulunamadı."
      );
      return;
    }

    try {
      const response = await axiosInstance.get(
        "/islem-log-detay/son-iscilik",
        {
          params: {
            hesapId: Number(master.hesapId),
            stokId: Number(stokIdNum),
            islemTur: master.islemTur,
          },
          headers: {
            "X-User-Id": String(currentUserId),
          },
        }
      );

      const data = response?.data;

      setSatirlar((prev) =>
        prev.map((row, i) => {
          if (i !== index) {
            return row;
          }

          /*
           * İstek devam ederken kullanıcı başka stok seçmiş olabilir.
           * Eski isteğin yeni seçimi ezmesini engelle.
           */
          if (Number(row.stokId) !== Number(stokIdNum)) {
            return row;
          }

          /*
           * Aynı hesap + stok + işlem türü için geçmiş işçilik yoksa
           * işçilik alanları boş kalsın.
           */
          if (data?.bulundu !== true || data?.iscilik == null) {
            return {
              ...row,
              iscilik: "",
              iscilikGram: "",
            };
          }

          const sonIscilik = Number(data.iscilik);

          if (!Number.isFinite(sonIscilik)) {
            return {
              ...row,
              iscilik: "",
              iscilikGram: "",
            };
          }

          const miktar = Number(row.miktar) || 0;
          const adet = Number(row.adet) || 0;

          let iscilikGram = "";

          if (sonIscilik > 0 && (adet > 0 || miktar > 0)) {
            const g =
              adet > 0
                ? adet * (sonIscilik / 100)
                : miktar * (sonIscilik / 1000);

            iscilikGram = g.toFixed(2);
          }

          return {
            ...row,
            iscilik: String(sonIscilik),
            iscilikGram,
          };
        })
      );
    } catch (error) {
      console.error("Son işçilik isteği başarısız:", {
        status: error?.response?.status,
        data: error?.response?.data,
        message: error?.message,
        baseURL: error?.config?.baseURL,
        url: error?.config?.url,
        params: error?.config?.params,
      });
    }
  };

  /* ===========================================================
     SATIR DEĞİŞİKLİĞİ
  =========================================================== */

  const handleSatirChange = (index, field, value) => {
    setSatirlar((prev) => {
      const list = [...prev];

      const row = {
        ...list[index],
        [field]: value,
      };

      const miktar = Number(row.miktar) || 0;

      const milyem = Number(row.milyem) || 0;

      const iscilik = Number(row.iscilik) || 0;

      const adet = Number(row.adet) || 0;

      /* =====================================================
           HAS GRAM
        ===================================================== */

      if (field !== "hasGram") {
        if (miktar > 0 && milyem > 0) {
          row.hasGram = (miktar * (milyem / 1000)).toFixed(2);
        } else {
          row.hasGram = "";
        }
      }

      /* =====================================================
           İŞÇİLİK GRAM
        ===================================================== */

      if (field !== "iscilikGram") {
        if (iscilik > 0 && (adet > 0 || miktar > 0)) {
          const g =
            adet > 0 ? adet * (iscilik / 100) : miktar * (iscilik / 1000);

          row.iscilikGram = g.toFixed(2);
        } else {
          row.iscilikGram = "";
        }
      }

      list[index] = row;

      return list;
    });
  };

  /* ===========================================================
     TOPLAMLAR
  =========================================================== */

  const toplamHasGram = satirlar.reduce(
    (sum, s) => sum + (Number(s.hasGram) || 0),
    0
  );

  const toplamIscilikGram = satirlar.reduce(
    (sum, s) => sum + (Number(s.iscilikGram) || 0),
    0
  );

  const toplamGenel = toplamHasGram + toplamIscilikGram;

  /* ===========================================================
     KAYDI ÇALIŞTIR
  =========================================================== */

  const executeSave = async (payload) => {
    try {
      setLoading(true);

      await axiosInstance.put("/islemler/altin/updateDetay", payload);

      /*
       * Başarılı kayıttan sonra mevcut değerler
       * yeni referans durum olsun.
       */
      setInitialMaster({
        ...master,
      });

      setInitialSatirlar(
        satirlar.map((row) => ({
          ...row,
        }))
      );

      alert("Kaydedildi.");

      navigate(-1);
    } catch (err) {
      console.error("Kaydetme hatası:", err);

      alert("Kaydetme sırasında hata oluştu.");
    } finally {
      setLoading(false);

      setPendingSavePayload(null);

      setBosSatirKaydetPopupOpen(false);
    }
  };

  /* ===========================================================
     SAVE
  =========================================================== */

  const handleSave = async () => {
    if (!canUpdate) {
      alert("Düzenleme yetkiniz yok.");

      return;
    }

    if (!master.id) {
      alert("Kayıt bulunamadı.");

      return;
    }

    /* =====================================================
         ALTIN VERİLDİ:
         ALINAN İŞÇİLİK KONTROLÜ
      ===================================================== */

    if (master.islemTur === "ALTIN_VERILDI") {
      for (const row of satirlar) {
        if (
          row.alinanIscilik !== "" &&
          row.alinanIscilik !== null &&
          row.alinanIscilik !== undefined
        ) {
          const value = Number(row.alinanIscilik);

          if (!Number.isFinite(value) || value < 0) {
            alert("İşçilik Maliyet 0 veya daha büyük bir sayı olmalıdır.");

            return;
          }
        }
      }
    }

/* =====================================================
         ALTIN ALINDI:
         ALINAN İŞÇİLİK KONTROLÜ
      ===================================================== */

    if (master.islemTur === "ALTIN_ALINDI") {
      for (const row of satirlar) {
        if (
          row.verilenIscilik !== "" &&
          row.verilenIscilik !== null &&
          row.verilenIscilik !== undefined
        ) {
          const value = Number(row.verilenIscilik);

          if (!Number.isFinite(value) || value < 0) {
            alert("işçilik İade 0 veya daha büyük bir sayı olmalıdır.");

            return;
          }
        }
      }
    }


    /* =====================================================
         PAYLOAD
      ===================================================== */

    const payload = {
      masterId: master.id,

      tarih: master.tarih,

      islemTur: master.islemTur,

      aciklama: master.aciklama,

      satirlar: satirlar.map((s) => ({
        id: s.id,

        stokId: s.stokId || null,

        miktar: s.miktar === "" ? null : Number(s.miktar),

        milyem: s.milyem === "" ? null : Number(s.milyem),

        hasGram: s.hasGram === "" ? null : Number(s.hasGram),

        adet: s.adet === "" ? null : Number(s.adet),

        iscilik: s.iscilik === "" ? null : Number(s.iscilik),

        iscilikGram: s.iscilikGram === "" ? null : Number(s.iscilikGram),

        alinanIscilik:
          master.islemTur === "ALTIN_VERILDI" &&
          s.alinanIscilik !== "" &&
          s.alinanIscilik !== null &&
          s.alinanIscilik !== undefined
            ? Number(s.alinanIscilik)
            : null,

        verilenIscilik:
                  master.islemTur === "ALTIN_ALINDI" &&
                  s.verilenIscilik !== "" &&
                  s.verilenIscilik !== null &&
                  s.verilenIscilik !== undefined
                    ? Number(s.verilenIscilik)
                    : null,

      })),
    };

    /* =====================================================
         HİÇ SATIR KALMADI
      ===================================================== */

    if (!payload.satirlar || payload.satirlar.length === 0) {
      setPendingSavePayload(payload);

      setBosSatirKaydetPopupOpen(true);

      return;
    }

    await executeSave(payload);
  };

  /* ===========================================================
     BOŞ SATIR ONAY
  =========================================================== */

  const handleConfirmBosSatirKaydet = async () => {
    if (!pendingSavePayload) {
      setBosSatirKaydetPopupOpen(false);

      return;
    }

    await executeSave(pendingSavePayload);
  };

  const handleCancelBosSatirKaydet = () => {
    setBosSatirKaydetPopupOpen(false);

    setPendingSavePayload(null);
  };

  /* ===========================================================
     TÜM İŞLEMİ SİL
  =========================================================== */

  const handleDeleteAllClick = () => {
    if (!canDelete) {
      return;
    }

    if (!master.id) {
      alert("Silinecek kayıt bulunamadı.");

      return;
    }

    setSilmePopupOpen(true);
  };

  const handleDeleteAllConfirmed = async () => {
    if (!canDelete) {
      alert("Silme yetkiniz yok.");

      setSilmePopupOpen(false);

      return;
    }

    if (!master.id) {
      return;
    }

    try {
      setLoading(true);

      await axiosInstance.delete(`/islemler/${master.id}`);

      alert("İşlem silindi.");

      navigate(-1);
    } catch (err) {
      console.error("Silme hatası:", err);

      alert("Silme sırasında hata oluştu.");
    } finally {
      setLoading(false);

      setSilmePopupOpen(false);
    }
  };

  /* ===========================================================
     FİŞ TARİH FORMAT
  =========================================================== */

  const formatFisTarih = (dtLocal) => {
    if (!dtLocal) {
      return "";
    }

    const d = new Date(dtLocal);

    if (Number.isNaN(d.getTime())) {
      return "";
    }

    return d.toLocaleString("tr-TR", {
      year: "numeric",

      month: "2-digit",

      day: "2-digit",

      hour: "2-digit",

      minute: "2-digit",
    });
  };

  /* ===========================================================
     FİŞ YAZDIR

     PC:
     HTML -> window.print()

     ANDROID:
     Web Bluetooth -> ESC/POS -> XP-80C
  =========================================================== */

  const handlePrintFis = async () => {
    /*
     * Mobil + Web Bluetooth destekleniyorsa
     * Bluetooth yazdırmayı kullan.
     */
    const bluetoothPrint = shouldUseBluetoothPrint();

    /* =====================================================
         BLUETOOTH BAĞLANTISI

         requestDevice kullanıcı tıklamasına bağlı çağrılmalıdır.

         Bu nedenle başka herhangi bir await işleminden
         ÖNCE bağlantıyı açıyoruz.
      ===================================================== */

    if (bluetoothPrint && !isBluetoothPrinterConnected()) {
      try {
        await connectBluetoothPrinter();
      } catch (bluetoothError) {
        console.error(
          "XP-80C Bluetooth bağlantısı kurulamadı:",
          bluetoothError
        );

        /*
         * Bağlantı kurulamazsa aşağıda
         * standart HTML yazdırma açılacak.
         */
      }
    }

    /* =====================================================
         FİŞ MODELİ
      ===================================================== */

    const fis = {
      altin: (satirlar || []).map((s) => ({
        /*
         * A / V için gerekli.
         */
        islemTur: master?.islemTur || "",

        stokAdi: s?.stokAdi || "",

        miktar: s?.miktar ?? 0,

        milyem: s?.milyem ?? 0,

        hasGram: s?.hasGram ?? 0,

        adet: s?.adet ?? 0,

        iscilik: s?.iscilik ?? 0,

        iscilikGram: s?.iscilikGram ?? 0,

        alinanIscilik: s?.alinanIscilik ?? 0,

        verilenIscilik: s?.verilenIscilik ?? 0,

      })),

      ziynet: [],

      nakit: [],

      gramKarNakit: [],
    };

    /* =====================================================
         ÖNCEKİ BAKİYE
      ===================================================== */

    const onceki = {
      gram:
        typeof oncekiBakiye === "number"
          ? oncekiBakiye
          : Number(oncekiBakiye) || 0,

      tl: 0,

      usd: 0,

      eur: 0,
    };

    /* =====================================================
         SONRAKİ BAKİYE
      ===================================================== */

    const son = {
      gram:
        typeof sonrakiBakiye === "number"
          ? sonrakiBakiye
          : Number(sonrakiBakiye) || 0,

      tl: 0,

      usd: 0,

      eur: 0,
    };

    const tarihStr = formatFisTarih(master?.tarih);

    /* =====================================================
         MOBİL + XP-80C BLUETOOTH
      ===================================================== */

    if (bluetoothPrint && isBluetoothPrinterConnected()) {
      try {
        await printBluetoothFis({
          fis,

          onceki,

          son,

          tarih: tarihStr,

          hesapAdi,

          firmaAdi,

          /*
           * Bu ekran sadece gram fişi olduğu için
           * ziynet bakiyesi yok.
           */
          ziynetBakiyeler: null,
        });

        /*
         * Bluetooth yazdırma başarılı.
         *
         * Popup açmaya gerek yok.
         */
        return;
      } catch (bluetoothPrintError) {
        console.error("XP-80C Bluetooth yazdırma hatası:", bluetoothPrintError);

        alert(
          "Bluetooth yazdırma başarısız oldu. Standart yazdırma ekranı açılacak."
        );
      }
    }

    /* =====================================================
         MASAÜSTÜ / FALLBACK HTML YAZDIRMA
      ===================================================== */

    try {
      const html = buildFisHTML(fis, onceki, son, tarihStr, hesapAdi, firmaAdi);

      printHtmlFallback(html);
    } catch (printError) {
      console.error("Fiş yazdırma hatası:", printError);

      alert(printError?.message || "Fiş yazdırılırken bir hata oluştu.");
    }
  };

  /* ===========================================================
     UI
  =========================================================== */

  return (
    <div className="kuyumcu-page-container">
      <PremiumBackground />

      <KuyumcuLeftSideNavbar />

      <div className="kuyumcu-page-content">
        {/* =====================================================
            ÜST BAR
        ===================================================== */}

        <Box
          mb={3}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <ProsetBackButton label="Geri Dön" />
        </Box>

        <Box
          mb={2}
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          flexWrap="wrap"
          gap={2}
        >
          <Box>
            <Typography variant="h5" className="page-title">
              Altın İşlem Detayı
            </Typography>

            {hesapAdi ? (
              <Typography
                variant="body2"
                sx={{
                  mt: 0.5,
                  color: "rgba(122, 90, 0, 0.85)",
                }}
              >
                • {hesapAdi} •
              </Typography>
            ) : null}
          </Box>
        </Box>

        {/* =====================================================
            MASTER
        ===================================================== */}

        <Paper className="altin-detay-master-card" elevation={3}>
          <Box display="flex" flexWrap="wrap" gap={2}>
            <TextField
              label="Tarih / Saat"
              type="datetime-local"
              value={master.tarih}
              onChange={(e) =>
                setMaster((p) => ({
                  ...p,

                  tarih: e.target.value,
                }))
              }
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              disabled={!canUpdate}
            />

            <TextField
              select
              label="İşlem Türü"
              value={master.islemTur}
              onChange={(e) => {
                const yeniIslemTur = e.target.value;

                setMaster((p) => ({
                  ...p,

                  islemTur: yeniIslemTur,
                }));

                /*
                 * ALTIN_VERILDI değilse
                 * alınan işçilik temizlensin.
                 */
                if (yeniIslemTur !== "ALTIN_VERILDI") {
                  setSatirlar((prev) =>
                    prev.map((row) => ({
                      ...row,

                      alinanIscilik: "",
                    }))
                  );
                }

                /*
                 * ALTIN_ALINDI değilse
                 * verilen işçilik temizlensin.
                 */
                if (yeniIslemTur !== "ALTIN_ALINDI") {
                  setSatirlar((prev) =>
                    prev.map((row) => ({
                      ...row,

                      verilenIscilik: "",
                    }))
                  );
                }
              }}
              fullWidth
              InputLabelProps={{
                shrink: true,
              }}
              disabled={!canUpdate}
            >
              <MenuItem value="ALTIN_ALINDI">
                {renderIslemTurLabel("ALTIN_ALINDI")}
              </MenuItem>

              <MenuItem value="ALTIN_VERILDI">
                {renderIslemTurLabel("ALTIN_VERILDI")}
              </MenuItem>
            </TextField>
          </Box>

          <Box mt={2}>
            <TextField
              label="Açıklama"
              value={master.aciklama}
              onChange={(e) =>
                setMaster((p) => ({
                  ...p,

                  aciklama: e.target.value,
                }))
              }
              fullWidth
              multiline
              minRows={2}
              disabled={!canUpdate}
            />
          </Box>
        </Paper>

        {/* =====================================================
            SATIRLAR
        ===================================================== */}

        <Box mt={3}>
          <Box
            mb={2}
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
          >
            <Typography variant="h5" className="page-title">
              Altın Satırları
            </Typography>

            {/* UPDATE varsa satır ekle görünür */}

            {canUpdate && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddRow}
                disabled={loading || permLoading}
                sx={{
                  background: "linear-gradient(135deg, #2E8B57, #12a37c)",

                  color: "white",

                  fontWeight: 700,

                  borderRadius: "14px",

                  px: 2.2,

                  "&:hover": {
                    background: "linear-gradient(135deg, #12a37c, #0f8d6b)",
                  },
                }}
              >
                Satır Ekle
              </Button>
            )}
          </Box>

          {/* ===================================================
              BOŞ SATIR DURUMU
          =================================================== */}

          {satirlar.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Henüz satır yok.
              {canUpdate
                ? ' "Satır Ekle" butonu ile yeni satır ekleyebilirsiniz.'
                : ""}
            </Typography>
          ) : null}

          {/* ===================================================
              SATIR KARTLARI
          =================================================== */}

          <Box display="flex" flexDirection="column" gap={2}>
            {satirlar.map((row, index) => (
              <Paper
                key={`${row.id ?? "new"}-${index}`}
                className="altin-detay-row-card"
                elevation={1}
              >
                <Box display="flex" flexWrap="wrap" gap={2} alignItems="center">
                  {/* ========================================
                        STOK
                    ======================================== */}

                  <FormControl
                    sx={{
                      minWidth: 220,

                      flex: 1,
                    }}
                  >
                    <InputLabel shrink>Stok</InputLabel>

                    <Select
                      value={
                        row.stokId === null || row.stokId === undefined
                          ? ""
                          : row.stokId
                      }
                      label="Stok"
                      onChange={(e) => handleStokChange(index, e.target.value)}
                      displayEmpty
                      disabled={!canUpdate}
                    >
                      <MenuItem value="">
                        <em>Seçiniz</em>
                      </MenuItem>

                      {stoklar
                        .filter((s) => !s.sabitMi)
                        .map((s) => (
                          <MenuItem key={s.id} value={s.id}>
                            {s.stokAdi}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>

                  {/* ========================================
                        MİKTAR
                    ======================================== */}

                  <TextField
                    label="Miktar (gr)"
                    type="number"
                    value={row.miktar}
                    onChange={(e) =>
                      handleSatirChange(index, "miktar", e.target.value)
                    }
                    sx={{
                      width: 140,
                    }}
                    disabled={!canUpdate}
                  />

                  {/* ========================================
                        MİLYEM
                    ======================================== */}

                  <TextField
                    label="Milyem"
                    type="number"
                    value={row.milyem}
                    onChange={(e) =>
                      handleSatirChange(index, "milyem", e.target.value)
                    }
                    sx={{
                      width: 130,
                    }}
                    disabled={!canUpdate}
                  />

                  {/* ========================================
                        HAS
                    ======================================== */}

                  <TextField
                    label="Has (gr)"
                    type="number"
                    value={row.hasGram}
                    sx={{
                      width: 140,
                    }}
                    disabled
                  />

                  {/* ========================================
                        ADET
                    ======================================== */}

                  <TextField
                    label="Adet"
                    type="number"
                    value={row.adet}
                    onChange={(e) =>
                      handleSatirChange(index, "adet", e.target.value)
                    }
                    sx={{
                      width: 110,
                    }}
                    disabled={!canUpdate}
                  />

                  {/* ========================================
                        İŞÇİLİK
                    ======================================== */}

                  <TextField
                    label="İşçilik"
                    type="number"
                    value={row.iscilik}
                    onChange={(e) =>
                      handleSatirChange(index, "iscilik", e.target.value)
                    }
                    sx={{
                      width: 130,
                    }}
                    disabled={!canUpdate}
                  />

                  {/* ========================================
                        İŞÇİLİK GRAM
                    ======================================== */}

                  <TextField
                    label="İşçilik (gr)"
                    type="number"
                    value={row.iscilikGram}
                    sx={{
                      width: 150,
                    }}
                    disabled
                  />

                  {/* ========================================
                        ALINAN İŞÇİLİK
                    ======================================== */}

                  {master.islemTur === "ALTIN_VERILDI" && (
                    <TextField
                      label="İşçilik Maliyet"
                      type="number"
                      value={row.alinanIscilik}
                      onChange={(e) =>
                        handleSatirChange(
                          index,
                          "alinanIscilik",
                          e.target.value
                        )
                      }
                      inputProps={{
                        min: 0,
                        step: "any",
                      }}
                      sx={{
                        width: 160,
                      }}
                      disabled={!canUpdate}
                    />
                  )}

                  {/* ========================================
                        VERİLEN İŞÇİLİK
                    ======================================== */}

                  {master.islemTur === "ALTIN_ALINDI" && (
                    <TextField
                      label="İşçilik (İade Durumu)"
                      type="number"
                      value={row.verilenIscilik}
                      onChange={(e) =>
                        handleSatirChange(
                          index,
                          "verilenIscilik",
                          e.target.value
                        )
                      }
                      inputProps={{
                        min: 0,
                        step: "any",
                      }}
                      sx={{
                        width: 160,
                      }}
                      disabled={!canUpdate}
                    />
                  )}

                  {/* ========================================
                        SATIR SİL
                    ======================================== */}

                  {canUpdate && (
                    <IconButton
                      color="error"
                      onClick={() => handleDeleteRow(index)}
                      disabled={loading || permLoading}
                      title="Satırı Sil"
                    >
                      <Delete />
                    </IconButton>
                  )}
                </Box>
              </Paper>
            ))}
          </Box>
        </Box>

        {/* =====================================================
            TOPLAMLAR
        ===================================================== */}

        <Paper className="altin-detay-toplam-card" elevation={2}>
          <Box display="flex" flexWrap="wrap" gap={3}>
            <TextField
              label="Toplam Has Gram Altın"
              value={toplamHasGram.toFixed(2)}
              InputProps={{
                readOnly: true,
              }}
              sx={{
                minWidth: 240,
              }}
            />

            <TextField
              label="Toplam İşçilik Gram"
              value={toplamIscilikGram.toFixed(2)}
              InputProps={{
                readOnly: true,
              }}
              sx={{
                minWidth: 240,
              }}
            />

            <TextField
              label="Toplam"
              value={toplamGenel.toFixed(2)}
              InputProps={{
                readOnly: true,
              }}
              sx={{
                minWidth: 240,
              }}
            />
          </Box>
        </Paper>

        {/* =====================================================
            ALT BUTONLAR
        ===================================================== */}

        <Box
          mt={3}
          mb={1}
          display="flex"
          flexWrap="wrap"
          justifyContent="flex-end"
          gap={2}
        >
          {/* ===================================================
              KAYDET
          =================================================== */}

          {canUpdate && hasChanges && (
            <Button
              startIcon={<SaveIcon />}
              variant="contained"
              color="success"
              onClick={handleSave}
              disabled={loading || permLoading}
              sx={{
                borderRadius: "14px",

                fontWeight: 700,

                px: 2.4,
              }}
              title="Kaydet"
            >
              Kaydet
            </Button>
          )}

          {/* ===================================================
              FİŞ YAZDIR
          =================================================== */}

          <Button
            variant="contained"
            onClick={handlePrintFis}
            startIcon={<Print />}
            disabled={loading}
            sx={{
              borderRadius: "14px",

              fontWeight: 700,

              px: 2.4,

              background: "linear-gradient(135deg, #f1c40f, #d4ac0d)",

              "&:hover": {
                background: "linear-gradient(135deg, #d4ac0d, #b7950b)",
              },
            }}
          >
            Fiş Yazdır
          </Button>

          {/* ===================================================
              SİL
          =================================================== */}

          {canDelete && (
            <Button
              startIcon={<DeleteIcon />}
              variant="contained"
              color="error"
              onClick={handleDeleteAllClick}
              disabled={loading || permLoading}
              sx={{
                borderRadius: "14px",

                fontWeight: 700,

                px: 2.4,
              }}
            >
              Sil
            </Button>
          )}
        </Box>
      </div>

      {/* =======================================================
          SİLME ONAY POPUP
      ======================================================= */}

      <SilmeUyariPopup
        open={silmePopupOpen}
        title="Silme Onayı"
        message="Bu altın işlemi ve tüm satırları silinecek. Emin misiniz?"
        onCancel={() => setSilmePopupOpen(false)}
        onConfirm={handleDeleteAllConfirmed}
      />

      {/* =======================================================
          BOŞ SATIR KAYDET ONAY
      ======================================================= */}

      <SilmeUyariPopup
        open={bosSatirKaydetPopupOpen}
        message="Hiç satır kalmadı. Kaydetmeye devam edersen ana işlem kaydı da tamamen silinecek. Emin misiniz?"
        onCancel={handleCancelBosSatirKaydet}
        onConfirm={handleConfirmBosSatirKaydet}
      />
    </div>
  );
}